import { twJoin } from 'tailwind-merge';

import { Badge, BadgeText } from '@/components/ui/badge';
import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';

export type UnderlineTab<K extends string> = {
  key: K;
  label: string;
  /**
   * Shown as a muted count badge when above zero, its text following the
   * label: brand green on the active tab, gray on the others. The gray is
   * typography-700 rather than the label's 600, which falls to 4.45:1 on the
   * badge's muted fill.
   */
  count?: number;
  testID?: string;
};

/** Visual height of a tab: text line, padding and underline. */
const TAB_HEIGHT = 32;

interface UnderlineTabsProps<K extends string> {
  tabs: UnderlineTab<K>[];
  activeKey: K;
  onChange: (key: K) => void;
  /** Layout classes for the bar, such as horizontal padding. */
  className?: string;
}

/**
 * A row of text tabs with an underline on the active one and an optional count
 * badge, sitting on a hairline divider. Composed from gluestack primitives,
 * since gluestack ships no tabs component.
 *
 * Shared so every tabbed surface reads the same: Fellows Corner's mentor
 * detail and the expert profile's clinical trials both use it.
 */
export function UnderlineTabs<K extends string>({
  tabs,
  activeKey,
  onChange,
  className,
}: UnderlineTabsProps<K>) {
  return (
    <HStack
      className={twJoin('gap-6 border-b border-outline-100', className)}
      accessibilityRole="tablist">
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey;

        return (
          <Pressable
            key={tab.key}
            testID={tab.testID}
            onPress={() => onChange(tab.key)}
            hitSlop={getAccessibleHitSlop(TAB_HEIGHT)}
            accessibilityRole="tab"
            accessibilityState={{ selected: isActive }}
            className={twJoin(
              '-mb-px flex-row items-center gap-2 border-b-2 pb-2 pt-1 web:cursor-pointer',
              isActive ? 'border-primary-700' : 'border-transparent web:hover:border-outline-300'
            )}>
            <Text
              size="sm"
              className={twJoin(
                'web:transition-colors',
                isActive
                  ? 'font-semibold text-primary-700'
                  : 'font-medium text-typography-600 web:hover:text-typography-900'
              )}>
              {tab.label}
            </Text>
            {tab.count ? (
              <Badge size="sm" action="muted">
                <BadgeText className={isActive ? 'text-primary-700' : 'text-typography-700'}>
                  {tab.count}
                </BadgeText>
              </Badge>
            ) : null}
          </Pressable>
        );
      })}
    </HStack>
  );
}
